"use client";

import * as React from "react";
import { CheckCircle2, X, XCircle } from "lucide-react";
import { toast as sonnerToast } from "sonner";

import { cn } from "@/app/_lib/utils";

export interface GlassToastProps {
  id: string | number;
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: "default" | "destructive";
}

/**
 * Toast card body: the `.liquid-glass` CSS baseline (see globals.css) rather
 * than the full `<Glass>` refraction wrapper — toasts float over arbitrary
 * page backgrounds (including plain white sections), so the branded `<Glass>`
 * button chrome (white text, tuned for dark/colour CTA surfaces) would go
 * unreadable here. Dark text on a frosted card reads correctly anywhere.
 */
export function GlassToastCard({
  id,
  title,
  description,
  variant = "default",
}: GlassToastProps) {
  const isError = variant === "destructive";

  return (
    <div
      className={cn(
        "liquid-glass pointer-events-auto flex w-[min(380px,90vw)] items-start gap-3 rounded-2xl border-l-4 px-4 py-3.5",
        isError ? "border-l-red-500" : "border-l-primary",
      )}
    >
      {isError ? (
        <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
      ) : (
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
      )}
      <div className="min-w-0 flex-1">
        {title && (
          <p className="text-sm font-semibold text-foreground">{title}</p>
        )}
        {description && (
          <p className="mt-0.5 text-xs text-muted-foreground">
            {description}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => sonnerToast.dismiss(id)}
        className="shrink-0 text-muted-foreground/60 transition-colors hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
